import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { idToIso } from '../helpers';
import { getLanguageFromCookie, setLanguageOnCookie } from '../helpers/cookieLanguage';
import { setDocumentLanguage } from '../helpers/setDocumentLanguage';
import { setSupportedLanguages } from '../redux/appreadouts/actions';
import { IRootState } from '../redux';
import { changeLanguage } from '../redux/user/actions';
import { IUserState } from '../redux/user/interfaces';

const FALLBACK_LANGUAGE = 'da';
const FALLBACK_LANGUAGE_ID = 2;

const SUPPORTED_LANGUAGES = [
    {
        id: 1,
        name: 'English',
        localName: 'English',
        isoCode: 'en',
    },
    {
        id: 2,
        name: 'Danish',
        localName: 'Dansk',
        isoCode: 'da',
    },
];

const UNSUPPORTED_LANGUAGE_MAP = new Map([['kl', 'da']]);

export const useLanguage = (): string => {
    const dispatch = useDispatch();
    const { languageId } = useSelector<IRootState, IUserState>(({ user }) => user);

    const selectedLanguage = idToIso(SUPPORTED_LANGUAGES, languageId);

    useEffect(() => {
        const isoLanguage = getLanguageByPrecedence();
        const selectedLanguageId =
            SUPPORTED_LANGUAGES.find((lang) => lang.isoCode === isoLanguage)?.id || FALLBACK_LANGUAGE_ID;

        dispatch(setSupportedLanguages(SUPPORTED_LANGUAGES));
        dispatch(changeLanguage(selectedLanguageId));
    }, []);

    useEffect(() => {
        if (!languageId) {
            return;
        }

        setDocumentLanguage(SUPPORTED_LANGUAGES, languageId);
        setLanguageOnCookie(idToIso(SUPPORTED_LANGUAGES, languageId));
    }, [languageId]);

    return selectedLanguage;
};

const getLanguageByPrecedence = (): string => {
    const cookieIsoLanguage = getLanguageFromCookie();

    const hasCookieLanguage = Boolean(cookieIsoLanguage);

    if (hasCookieLanguage) {
        return mapUnsupportedLanguages(cookieIsoLanguage);
    }

    return FALLBACK_LANGUAGE;
};

const mapUnsupportedLanguages = (isoCode: string): string =>
    UNSUPPORTED_LANGUAGE_MAP.has(isoCode) ? UNSUPPORTED_LANGUAGE_MAP.get(isoCode) : isoCode;
